import { Button } from '@askable/ui/core/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@askable/ui/core/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@askable/ui/core/popover';
import _ from 'lodash';
import { ChevronDown } from 'lucide-react';
import momentTimezone from 'moment-timezone';
import { useRef, useState } from 'react';

import { MiniCalendar, LoadingOverlay } from 'components/common';
import { useConnectedClient } from 'context/ConnectedClientContext';
import { bookingUtils } from 'lib/booking';
import { utils } from 'lib/utils';

import type { Booking } from 'containers/Booking/types';
import type { Moment } from 'moment-timezone';

import './sessionToAddPopover.scss';

type SessionProps = {
  _id?: string;
  start: number;
  end: number;
  _researcher_user_id: string | null;
};
type Props = {
  sessionToAdd?: SessionProps;
  timezone: string;
  booking_id?: string;
  booking?: Booking;
  sessionDuration?: number;
  onClose: () => void;
  onAddSession: (session: SessionProps) => boolean | Promise<boolean>;
};

type HoursMinutesDay = { value: string; hour: number; minutes: number };

function SessionToAddPopover(props: Props) {
  const { details } = useConnectedClient();
  const [openMiniCalendarPopover, setOpenMiniCalendarPopover] = useState(false);
  const [dateToAdd, setDateToAdd] = useState<Moment | null>(
    props?.sessionToAdd?.start ? momentTimezone(props?.sessionToAdd?.start).tz(props.timezone) : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  // Synchronous guard so a double-click can't fire a second create before the isLoading state re-renders
  const isAddingRef = useRef(false);
  const hoursMinutesDay = utils.getHoursMinutesDay();
  const isBookingLive = bookingUtils.isActive(props.booking);
  const isBookingCompleted = bookingUtils.isCompleted(props.booking);

  if (!props.sessionToAdd || !props.sessionToAdd.start) {
    return null;
  }

  const onAddSession = async () => {
    if (!dateToAdd || isAddingRef.current) return null;
    isAddingRef.current = true;

    setIsLoading(true);
    const sessionToAdd = {
      start: dateToAdd.valueOf(),
      end: momentTimezone(dateToAdd).add(props.sessionDuration, 'minutes').valueOf(),
      _researcher_user_id: details?._id!,
    };

    try {
      // Await the create so a double-click can't fire the mutation twice, and only close once it
      // actually succeeded — a skipped (in-flight) or failed add returns false and keeps the dialog open
      const didAdd = await props.onAddSession(sessionToAdd);
      if (didAdd) props.onClose();
    } finally {
      setIsLoading(false);
      isAddingRef.current = false;
    }
  };

  const handleScrollToSelectedTime = (isOpen: boolean) => {
    if (!isOpen) {
      return;
    }
    const timeSelected = momentTimezone(dateToAdd).tz(props.timezone).format('h:mm a');
    setTimeout(() => {
      const element = document.getElementById(`time-${timeSelected}`);
      if (element) element.scrollIntoView();
    }, 150);
  };

  const handleSelectTime = (item: HoursMinutesDay) => {
    const m = momentTimezone(dateToAdd).tz(props.timezone);
    const newSelectedTime = m.hour(item.hour).minutes(item.minutes).valueOf();
    setDateToAdd(momentTimezone(newSelectedTime).tz(props.timezone));
  };

  return (
    <div className="sessionToAddComponent">
      {isLoading && <LoadingOverlay style={{ opacity: 0.8 }} />}
      <h3>Add session slot</h3>
      <div className="timeToAddContainer">
        <Popover open={openMiniCalendarPopover} onOpenChange={setOpenMiniCalendarPopover}>
          <PopoverTrigger>
            <div
              id="sessionToAddComponent_dayContainer"
              className="dayContainer"
              onClick={() => setOpenMiniCalendarPopover(true)}
            >
              <p>{momentTimezone(dateToAdd).tz(props.timezone).format('D MMM, YYYY')}</p>
              <ChevronDown color="#444" style={{ height: 20, marginTop: 4 }} />
            </div>
          </PopoverTrigger>
          <PopoverContent align="start" side="bottom">
            <MiniCalendar
              timezone={props.timezone}
              month={momentTimezone(dateToAdd).tz(props.timezone).month()}
              year={momentTimezone(dateToAdd).tz(props.timezone).year()}
              selectedDay={dateToAdd}
              onClickDay={(day: any) => {
                const selectedDay = momentTimezone(day).date();
                const selectedMonth = momentTimezone(day).month();
                const selectedYear = momentTimezone(day).year();
                const m = momentTimezone(dateToAdd).date(selectedDay);
                const newDaySelected = m.month(selectedMonth).year(selectedYear).valueOf();
                setDateToAdd(momentTimezone(newDaySelected).tz(props.timezone));
                setOpenMiniCalendarPopover(false);
              }}
              disablePastDates
            />
          </PopoverContent>
        </Popover>
        <DropdownMenu onOpenChange={handleScrollToSelectedTime}>
          <DropdownMenuTrigger asChild>
            <div id="sessionToAddComponent_hourContainer" className="hourContainer">
              <p>{momentTimezone(dateToAdd).tz(props.timezone).format('h:mm a')}</p>
              <ChevronDown color="#444" style={{ height: 20, marginTop: 4 }} />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {_.map(hoursMinutesDay, item => {
              const isSelected = item.value === momentTimezone(dateToAdd).tz(props.timezone).format('h:mm a');
              return (
                <DropdownMenuItem
                  asChild
                  id={`time-${item.value}`}
                  key={`time-${item.value}`}
                  className="w-full cursor-pointer px-2 py-1 text-sm hover:bg-accent"
                  onClick={() => handleSelectTime(item)}
                >
                  <span className={isSelected ? 'selected' : ''}>{item.value}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="bottomContainer">
        <div className="alertContainer">
          {(isBookingLive || isBookingCompleted) && (
            <>
              <img alt="icon-calendar" src="/icons/calendarInterrogation.svg" className="calendarIcon" />
              <p>Existing applicant availability unknown</p>
            </>
          )}
        </div>
        <Button variant="primary" disabled={isLoading} onClick={() => void onAddSession()}>
          Add
        </Button>
      </div>
    </div>
  );
}

export default SessionToAddPopover;
